import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { adminApi } from "../api/admin";
import type { Member } from "../api/types";
import { useAsync } from "../hooks/useAsync";
import { formatDate } from "../lib/format";
import { AdminTable, type Column } from "./AdminTable";
import { AdminForm, type FieldDef, type FormValues } from "./AdminForm";
import type { AdminOutletContext } from "./AdminLayout";
import { toNullableString } from "./formUtils";

const CREATE_FIELDS: FieldDef[] = [
  { name: "email", label: "Email", type: "text", required: true,
    help: "What they sign in with, and how photos and videos are shared with them. It can't be changed later." },
  { name: "displayName", label: "Name", type: "text", help: "Optional — shown in the console and to them." },
  { name: "password", label: "Starting password", type: "text", required: true,
    help: "At least 8 characters. Give it to them yourself; they can change it from their account page." },
];

const EDIT_FIELDS: FieldDef[] = [
  { name: "displayName", label: "Name", type: "text" },
  { name: "enabled", label: "Can sign in", type: "checkbox",
    help: "Turning this off signs them out at once. What was shared with them stays shared, ready if you turn it back on." },
  { name: "password", label: "Reset password", type: "text",
    help: "Leave blank to keep their password. A reset signs them out everywhere." },
];

/**
 * The people who can see non-public photos and videos. Members are created
 * here (there is no public sign-up); items are shared with them from the
 * Photos and Videos tabs, and they sign in on the public site.
 */
export function AdminMembers() {
  const { refreshToken } = useOutletContext<AdminOutletContext>();
  const [reload, setReload] = useState(0);
  const { data, loading, error } = useAsync(() => adminApi.listMembers(), [refreshToken, reload]);
  const rows = data ?? [];
  const [editing, setEditing] = useState<Member | null>(null);
  const [creating, setCreating] = useState(false);
  const refetch = () => setReload((n) => n + 1);

  const columns: Column<Member>[] = [
    {
      key: "name",
      header: "Name",
      className: "cell-title",
      render: (m) => m.displayName ?? <span className="cell-muted">—</span>,
      sortValue: (m) => m.displayName ?? "",
    },
    { key: "email", header: "Email", render: (m) => <span className="mono">{m.email}</span>, sortValue: (m) => m.email },
    {
      key: "status",
      header: "Status",
      render: (m) => (
        <span className={`pill ${m.enabled ? "pill--on" : "pill--off"}`}>{m.enabled ? "active" : "disabled"}</span>
      ),
      sortValue: (m) => (m.enabled ? 0 : 1),
    },
    {
      key: "shared",
      header: "Shared items",
      render: (m) => <span className="mono">{m.sharedItemCount}</span>,
      sortValue: (m) => m.sharedItemCount,
    },
    {
      key: "lastLogin",
      header: "Last sign-in",
      render: (m) => <span className="mono">{formatDate(m.lastLoginAt) || "never"}</span>,
      sortValue: (m) => m.lastLoginAt ?? "",
    },
    {
      key: "created",
      header: "Added",
      render: (m) => <span className="mono">{formatDate(m.createdAt)}</span>,
      sortValue: (m) => m.createdAt,
    },
  ];

  return (
    <>
      <p className="admin-intro">
        Members can see photos and videos marked <strong>Members</strong>, plus anything
        shared with them personally. They sign in from the sidebar on the public site.
      </p>
      <AdminTable
        rows={rows}
        getId={(m) => m.id}
        columns={columns}
        searchText={(m) => `${m.email} ${m.displayName ?? ""}`}
        searchPlaceholder="Filter members…"
        typeFilter={{
          label: "Status",
          value: (m) => (m.enabled ? "active" : "disabled"),
          options: [
            { value: "active", label: "Active" },
            { value: "disabled", label: "Disabled" },
          ],
        }}
        loading={loading}
        error={error ?? undefined}
        emptyLabel="No members yet. Add the people you want to share private photos and videos with."
        createLabel="+ New member"
        onCreate={() => setCreating(true)}
        onEdit={(m) => setEditing(m)}
        onDelete={async (m) => {
          await adminApi.deleteMember(m.id);
          refetch();
        }}
        bulkActions={[
          {
            label: "Disable selected",
            run: async (sel) => {
              for (const m of sel) await adminApi.updateMember(m.id, { displayName: m.displayName, enabled: false });
              refetch();
            },
          },
          {
            label: "Enable selected",
            run: async (sel) => {
              for (const m of sel) await adminApi.updateMember(m.id, { displayName: m.displayName, enabled: true });
              refetch();
            },
          },
        ]}
      />
      {creating && (
        <AdminForm
          title="New member"
          fields={CREATE_FIELDS}
          initial={{ email: "", displayName: "", password: "" }}
          submitLabel="Create"
          onCancel={() => setCreating(false)}
          onSubmit={async (v: FormValues) => {
            await adminApi.createMember({
              email: String(v.email ?? "").trim(),
              displayName: toNullableString(v.displayName),
              password: String(v.password ?? ""),
            });
            setCreating(false);
            refetch();
          }}
        />
      )}
      {editing && (
        <AdminForm
          title={`Edit ${editing.email}`}
          fields={EDIT_FIELDS}
          initial={{ displayName: editing.displayName ?? "", enabled: editing.enabled, password: "" }}
          onCancel={() => setEditing(null)}
          onSubmit={async (v: FormValues) => {
            await adminApi.updateMember(editing.id, {
              displayName: toNullableString(v.displayName),
              enabled: Boolean(v.enabled),
              password: toNullableString(v.password),
            });
            setEditing(null);
            refetch();
          }}
        />
      )}
    </>
  );
}
